import { useState, useEffect, useCallback } from "react";
import { EquipmentManagementTask } from "../../types/api";
import {
  useEquipmentManagementStore,
  useOrgUsersStore,
} from "../../store/store";
import { useAuth } from "../../auth/AuthProvider";

interface RemediationActionFormProps {
  equipmentId: string;
  initialData: EquipmentManagementTask;
  onCancel: () => void;
  mode: "" | "view" | "update";
  warrantyExpiryDate: string;
  disabled?: boolean;
}

interface OrgUser {
  _id: { $oid: string };
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  mobile: string;
  premise?: string | Array<{ $oid: string }> | string[];
}

const RemediationActionForm = ({
  equipmentId,
  onCancel,
  mode,
  warrantyExpiryDate,
  initialData,
  disabled = false,
}: RemediationActionFormProps) => {
  const { user } = useAuth();
  const userRole = user?.roles?.[0];
  const { fetchOrgUsers, orgUsersData } = useOrgUsersStore();
  const { updateEquipmentManagementTaskHandler } =
    useEquipmentManagementStore();

  // Helper to get user ID as string (handles both object and string formats)
  const getUserIdString = useCallback(
    (userId: { $oid: string } | string | undefined): string => {
      if (!userId) return "";
      if (typeof userId === "string") return userId;
      return userId.$oid || "";
    },
    []
  );

  // Helper to find user ID from name or return the ID if it's already an ID
  const getResponsiblePersonId = useCallback(
    (responsiblePerson: string | undefined): string => {
      if (!responsiblePerson) return "";

      // Check if it's already an ID (try to find in orgUsersData)
      const userById = orgUsersData.find((u: OrgUser) => {
        const idStr = getUserIdString(u._id);
        return idStr === responsiblePerson;
      });
      if (userById) {
        return getUserIdString(userById._id);
      }

      // If it's a name, find the matching user
      const userByName = orgUsersData.find(
        (u: OrgUser) =>
          `${u.firstName} ${u.lastName}` === responsiblePerson ||
          responsiblePerson.includes(u.firstName) ||
          responsiblePerson.includes(u.lastName)
      );
      return userByName ? getUserIdString(userByName._id) : responsiblePerson;
    },
    [orgUsersData, getUserIdString]
  );

  const [formData, setFormData] = useState<EquipmentManagementTask>(() => ({
    ...initialData,
    responsiblePerson: getResponsiblePersonId(initialData.responsiblePerson),
    actionDate: initialData.actionDate || "",
    remediationAction: initialData.remediationAction || "",
    notes: initialData.notes || "",
    status: initialData.status || "Scheduled",
  }));

  useEffect(() => {
    fetchOrgUsers();
  }, [fetchOrgUsers]);

  // Update form data when initialData or orgUsersData changes
  useEffect(() => {
    setFormData({
      ...initialData,
      responsiblePerson: getResponsiblePersonId(initialData.responsiblePerson),
      actionDate: initialData.actionDate || "",
      remediationAction: initialData.remediationAction || "",
      notes: initialData.notes || "",
      status: initialData.status || "Scheduled",
    });
  }, [initialData, getResponsiblePersonId, orgUsersData]);

  const handleChange = (
    field: keyof EquipmentManagementTask,
    value: string
  ) => {
    setFormData((prev: EquipmentManagementTask) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!disabled) {
      updateEquipmentManagementTaskHandler(equipmentId, formData);
      onCancel();
    }
  };

  return (
    <div className="bg-white w-full max-w-3xl rounded-xl shadow-lg max-h-[80vh] flex flex-col relative">
      {/* Scrollable Content */}
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {mode === "update"
            ? "Update Remediation Action"
            : mode === "view"
            ? "View Remediation Action"
            : "Create Remediation Action"}
        </h2>
        <form
          onSubmit={handleSubmit}
          id="remediationForm"
          className="flex flex-col gap-6 pb-24"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Action */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="action"
                className="text-sm font-medium text-gray-700"
              >
                Action <span className="text-red-500">*</span>
              </label>
              <select
                id="action"
                value={formData.remediationAction}
                onChange={(e) =>
                  handleChange("remediationAction", e.target.value)
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                disabled={disabled || userRole === "orguser" || mode === "view"}
              >
                <option value="">Select Action</option>
                <option value="Contact Supplier">Contact Supplier</option>
                <option value="Extend Warranty">Extend Warranty</option>
                <option value="Schedule Maintenance">
                  Schedule Maintenance
                </option>
                <option value="Replace Equipment">Replace Equipment</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Responsible Person - Changed to dropdown */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="responsiblePerson"
                className="text-sm font-medium text-gray-700"
              >
                Responsible Person <span className="text-red-500">*</span>
              </label>
              <select
                id="responsiblePerson"
                value={formData.responsiblePerson}
                onChange={(e) =>
                  handleChange("responsiblePerson", e.target.value)
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                disabled={disabled || userRole === "orguser" || mode === "view"}
              >
                <option value="">Select Responsible Person</option>
                {orgUsersData.map((user: OrgUser) => {
                  const userId = getUserIdString(user._id);
                  return (
                    <option key={userId} value={userId}>
                      {user.firstName} {user.lastName}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Date */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="date"
                className="text-sm font-medium text-gray-700"
              >
                Action Date <span className="text-red-500">*</span>
              </label>
              <input
                id="date"
                type="date"
                value={formData.actionDate}
                max={warrantyExpiryDate}
                onChange={(e) => handleChange("actionDate", e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                disabled={disabled || userRole === "orguser" || mode === "view"}
              />
            </div>
            {/* Status */}
            <div>
              <label
                htmlFor="status"
                className="text-sm font-medium text-gray-700"
              >
                Status <span className="text-red-500">*</span>
              </label>
              <select
                id="status"
                value={formData.status || "Scheduled"}
                onChange={(e) => handleChange("status", e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                disabled={userRole === "orgadmin" || mode === "view"}
              >
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            {/* Notes */}
            <div className="md:col-span-2 flex flex-col gap-2">
              <label
                htmlFor="notes"
                className="text-sm font-medium text-gray-700"
              >
                Notes
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                rows={3}
                placeholder="Add notes about the action taken"
                disabled={disabled || mode === "view"}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Sticky Footer Buttons */}
      <div className="flex justify-end gap-4 p-4 bg-[#5A7192] rounded-b-lg sticky bottom-0">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 rounded-full bg-[#8497B0] text-white hover:bg-teal-600 transition flex items-center gap-1"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="remediationForm"
          className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition flex items-center gap-1"
          disabled={disabled || mode === "view"}
        >
          {mode === "update" ? "Update" : mode === "view" ? "View" : "Create"}
        </button>
      </div>
    </div>
  );
};

export default RemediationActionForm;
